import { useEffect, useState } from "react";
import { Lock, Unlock, Save, CheckCircle2, Sparkles } from "lucide-react";
import { api } from "../lib/apiClient";
import { useApp, MONTHS } from "../lib/store";
import { PageHeader, GradeBadge, Avatar, Empty } from "../components/Shared";
import { useEmployees, EmployeePicker, Panel } from "../components/Filters";
import { scoreColor } from "../lib/constants";
import { toast } from "sonner";

export default function Monthly() {
  const { month, year, can, role, isEmployee, selfEmployeeId } = useApp();
  const employees = useEmployees();
  const [empId, setEmpId] = useState(isEmployee ? selfEmployeeId : "om");
  const [data, setData] = useState(null);
  const [scores, setScores] = useState([]);
  const [remarks, setRemarks] = useState("");

  const load = () => api.monthlyReview(empId, month, year).then((d) => {
    setData(d); setScores(d.review.kra_scores.map((k) => ({ ...k }))); setRemarks(d.review.overall_remarks || "");
  }).catch(() => {});
  useEffect(() => { if (empId) load(); }, [empId, month, year]);

  if (!data) return <div className="py-20 text-center text-muted-c">Loading…</div>;
  const { review, employee, aggregate, kind } = data;
  const locked = review.locked;
  const blockedForManager = role === "manager" && employee.managed_by === "admin_only";
  const canEdit = can.reviewEmployees && !blockedForManager;
  const total = scores.reduce((s, k) => s + (k.type === "auto" ? (k.auto_score || 0) : (Number(k.manual_score) || 0)), 0);
  const grade = total >= 90 ? "A" : total >= 70 ? "B" : total >= 50 ? "C" : "D";

  const setManual = (pid, v) => {
    const mx = scores.find((k) => k.param_id === pid)?.max_marks || 10;
    v = Math.max(0, Math.min(mx, Number(v) || 0));
    setScores(scores.map((k) => k.param_id === pid ? { ...k, manual_score: v } : k));
  };
  const setRemark = (pid, v) => setScores(scores.map((k) => k.param_id === pid ? { ...k, remarks: v } : k));

  const save = async () => {
    try {
      await api.saveMonthlyReview({ employee_id: empId, month, year, kra_scores: scores, overall_remarks: remarks });
      toast.success("Monthly review saved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const toggleLock = async () => {
    try {
      let reviewId = review.id;
      if (!locked) {
        const saved = await api.saveMonthlyReview({ employee_id: empId, month, year, kra_scores: scores, overall_remarks: remarks });
        reviewId = saved.id;
      }
      await api.lockReview(reviewId, !locked);
      toast.success(!locked ? "Review locked & approved" : "Review reopened"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  return (
    <div>
      <PageHeader title="Monthly KRA Review" subtitle={`${MONTHS.find((m) => m.v === month)?.n} ${year} · manual + automatic scoring`}
        exportModule="monthly-review" exportParams={{ employee_id: empId, month, year }}>
        <EmployeePicker value={empId} onChange={setEmpId} employees={employees} disabled={isEmployee} testid="monthly-employee-picker" />
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        <div className="lg:col-span-8">
          <Panel testid="monthly-header-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Avatar src={employee.profile_image} name={employee.name} size={48} />
                <div><p className="font-display font-bold text-lg">{employee.name}</p><p className="text-sm text-secondary">{employee.designation}</p></div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right"><p className="text-xs text-muted-c uppercase tracking-wider">Live Score</p><p className="mono font-bold text-3xl" style={{ color: scoreColor(total) }}>{total.toFixed(1)}</p></div>
                <GradeBadge grade={grade} size="lg" />
                {locked ? <span className="pill px-3 py-1.5 text-xs" style={{ background: "rgba(239,68,68,0.12)", color: "#EF4444", borderColor: "rgba(239,68,68,0.4)" }}><Lock size={13} /> Locked</span>
                  : <span className="pill px-3 py-1.5 text-xs" style={{ background: "rgba(245,158,11,0.12)", color: "#F59E0B", borderColor: "rgba(245,158,11,0.4)" }}>Draft</span>}
              </div>
            </div>
          </Panel>
        </div>
        <div className="lg:col-span-4">
          <Panel title={kind === "dev" ? "Dev Task Summary" : "Content Calendar Summary"} testid="monthly-auto-summary">
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="elevated rounded-lg p-3"><p className="mono font-bold text-xl">{aggregate.count}</p><p className="text-xs text-muted-c">Tasks</p></div>
              <div className="elevated rounded-lg p-3"><p className="mono font-bold text-xl text-emerald-500">{aggregate.on_time}</p><p className="text-xs text-muted-c">On Time</p></div>
              <div className="elevated rounded-lg p-3"><p className="mono font-bold text-xl text-red-500">{aggregate.delayed}</p><p className="text-xs text-muted-c">Delayed</p></div>
              <div className="elevated rounded-lg p-3"><p className="mono font-bold text-xl brand-text">{aggregate.avg_score}</p><p className="text-xs text-muted-c">Auto /10</p></div>
            </div>
          </Panel>
        </div>
      </div>

      <div className="card-panel overflow-x-auto mb-6" data-testid="monthly-table">
        <table className="data-table">
          <thead><tr><th>#</th><th>KRA Parameter</th><th>Type</th><th>Auto</th><th style={{ width: 130 }}>Manual</th><th>Final</th><th>Remarks</th></tr></thead>
          <tbody>
            {scores.map((k) => (
              <tr key={k.param_id}>
                <td className="mono text-muted-c">{k.param_id}</td>
                <td className="font-medium">{k.name}</td>
                <td><span className={`pill px-2 py-0.5 text-xs ${k.type === "auto" ? "brand-text" : "text-secondary"} border-subtle`}>{k.type}</span></td>
                <td className="mono">{k.type === "auto" ? k.auto_score?.toFixed(1) : "—"}</td>
                <td>
                  {k.type === "auto" ? <span className="text-muted-c text-xs">auto</span> :
                    <input type="number" min="0" max={k.max_marks || 10} step="0.5" disabled={locked || !canEdit} value={k.manual_score ?? 0}
                      onChange={(e) => setManual(k.param_id, e.target.value)} className="input-c py-1 mono" style={{ width: 90 }} data-testid={`kra-score-slider-${k.param_id}`} />}
                </td>
                <td className="mono font-bold" style={{ color: scoreColor((k.type === "auto" ? k.auto_score : Number(k.manual_score) || 0) / (k.max_marks || 10) * 100) }}>{(k.type === "auto" ? k.auto_score : Number(k.manual_score) || 0).toFixed(1)}<span className="text-muted-c text-xs">/{k.max_marks || 10}</span></td>
                <td><input disabled={locked || !canEdit} value={k.remarks || ""} onChange={(e) => setRemark(k.param_id, e.target.value)} placeholder="Add remark…" className="input-c py-1 text-xs" data-testid={`kra-remark-${k.param_id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Panel title="Overall Manager Remarks" testid="monthly-remarks">
        <textarea disabled={locked || !canEdit} value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3}
          className="input-c" placeholder="Summarize the month's performance…" data-testid="overall-remarks-input" />
        <div className="flex justify-end gap-2 mt-4">
          {canEdit && !locked && <button onClick={save} className="pill px-4 py-2 text-sm font-semibold surface border-strong hover:brand-text transition-colors" data-testid="submit-review-button"><Save size={15} /> Save Draft</button>}
          {canEdit && !locked && <button onClick={toggleLock} data-testid="review-lock-toggle" className="pill px-4 py-2 text-sm font-semibold brand-bg text-white"><CheckCircle2 size={15} /> Approve & Lock</button>}
          {locked && can.reopenReviews && <button onClick={toggleLock} data-testid="review-lock-toggle" className="pill px-4 py-2 text-sm font-semibold brand-bg text-white"><Unlock size={15} /> Reopen Review</button>}
        </div>
        {blockedForManager && <p className="text-xs text-amber-500 mt-2 text-right">{employee.name} can only be reviewed by a Super Admin.</p>}
        {locked && !can.reopenReviews && <p className="text-xs text-muted-c mt-2 text-right">This review is finalized. Only a Super Admin can reopen it.</p>}
        {isEmployee && <p className="text-xs text-muted-c mt-2 text-right">You are viewing your review. Scores are set by your manager.</p>}
      </Panel>
    </div>
  );
}
